package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.EntityPreview
import top.wcpe.allincore.ui.runtime.element.Image
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.ProgressBar
import top.wcpe.allincore.ui.runtime.element.RadioButton
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import kotlin.jvm.JvmStatic
import kotlin.reflect.KClass

/**
 * Task 2a：控件视觉覆盖属性贡献者。
 *
 * 为 11 个交互 / 展示控件注册逐项视觉覆盖键（颜色 / 尺寸 / 圆角 / 状态层 overlay）。
 * 所有覆盖值写入元素的可空装箱字段，null = 回退主题 token，由对应渲染器解析。
 *
 * 边框 / 圆角 / 背景 / 渐变 / 阴影等键由 [VisualPropContributor] 注册到
 * style().visual()（SV 路径），各控件渲染器直接读取，无需在此重复登记。
 *
 * 内置 typed helper [colorKey] / [floatKey] / [intColorOrNull] /
 * [floatOrNull] 消除样板：每个键的差异只剩"键名 + 目标 setter"两处，
 * 类型判定与 null 透传逻辑收口在 helper 中。
 */
internal object ControlVisualPropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.ControlVisualPropContributor")

    @Suppress("CyclomaticComplexMethod", "LongMethod")
    @JvmStatic
    fun contribute(m: MutableMap<String, PropBinding>) {
        // ===== Checkbox =====
        colorKey(m, "checkColor", Checkbox::checkColor)
        colorKey(m, "focusBorderColor", Checkbox::focusBorderColor)
        floatKey(m, "checkInset", Checkbox::checkInset)
        floatKey(m, "disabledOpacity", Checkbox::disabledOpacity)
        colorKey(m, "hoverOverlayColor", Checkbox::hoverOverlayColor)
        floatKey(m, "hoverOverlayOpacity", Checkbox::hoverOverlayOpacity)
        colorKey(m, "pressedOverlayColor", Checkbox::pressedOverlayColor)
        floatKey(m, "pressedOverlayOpacity", Checkbox::pressedOverlayOpacity)
        colorKey(m, "focusOverlayColor", Checkbox::focusOverlayColor)
        floatKey(m, "focusOverlayOpacity", Checkbox::focusOverlayOpacity)
        colorKey(m, "boxBackgroundColor", Checkbox::boxBackgroundColor)
        colorKey(m, "boxBorderColor", Checkbox::boxBorderColor)

        // ===== RadioButton =====
        colorKey(m, "dotColor", RadioButton::dotColor)
        colorKey(m, "focusBorderColor", RadioButton::focusBorderColor)
        floatKey(m, "dotInset", RadioButton::dotInset)
        floatKey(m, "disabledOpacity", RadioButton::disabledOpacity)
        colorKey(m, "hoverOverlayColor", RadioButton::hoverOverlayColor)
        floatKey(m, "hoverOverlayOpacity", RadioButton::hoverOverlayOpacity)
        colorKey(m, "pressedOverlayColor", RadioButton::pressedOverlayColor)
        floatKey(m, "pressedOverlayOpacity", RadioButton::pressedOverlayOpacity)
        colorKey(m, "focusOverlayColor", RadioButton::focusOverlayColor)
        floatKey(m, "focusOverlayOpacity", RadioButton::focusOverlayOpacity)
        colorKey(m, "boxBackgroundColor", RadioButton::boxBackgroundColor)
        colorKey(m, "boxBorderColor", RadioButton::boxBorderColor)

        // ===== Slider =====
        colorKey(m, "trackColor", Slider::trackColor)
        colorKey(m, "fillColor", Slider::fillColor)
        colorKey(m, "thumbColor", Slider::thumbColor)
        floatKey(m, "trackHeight", Slider::trackHeight)
        floatKey(m, "thumbSize", Slider::thumbSize)
        floatKey(m, "trackCornerRadius", Slider::trackCornerRadius)
        colorKey(m, "arrowColor", Slider::arrowColor)
        colorKey(m, "stateLayerColor", Slider::stateLayerColor)
        floatKey(m, "stateLayerSize", Slider::stateLayerSize)
        colorKey(m, "hoverOverlayColor", Slider::hoverOverlayColor)
        floatKey(m, "hoverOverlayOpacity", Slider::hoverOverlayOpacity)
        colorKey(m, "pressedOverlayColor", Slider::pressedOverlayColor)
        floatKey(m, "pressedOverlayOpacity", Slider::pressedOverlayOpacity)
        colorKey(m, "focusOverlayColor", Slider::focusOverlayColor)
        floatKey(m, "focusOverlayOpacity", Slider::focusOverlayOpacity)

        // ===== ProgressBar =====
        colorKey(m, "notchColor", ProgressBar::notchColor)

        // ===== Switch =====
        colorKey(m, "trackColorOn", Switch::trackColorOn)
        colorKey(m, "trackColorOff", Switch::trackColorOff)
        colorKey(m, "thumbColorOn", Switch::thumbColorOn)
        colorKey(m, "thumbColorOff", Switch::thumbColorOff)
        floatKey(m, "thumbInset", Switch::thumbInset)
        floatKey(m, "trackCornerRadius", Switch::trackCornerRadius)
        floatKey(m, "thumbCornerRadius", Switch::thumbCornerRadius)
        colorKey(m, "hoverOverlayColor", Switch::hoverOverlayColor)
        floatKey(m, "hoverOverlayOpacity", Switch::hoverOverlayOpacity)
        colorKey(m, "pressedOverlayColor", Switch::pressedOverlayColor)
        floatKey(m, "pressedOverlayOpacity", Switch::pressedOverlayOpacity)
        colorKey(m, "focusOverlayColor", Switch::focusOverlayColor)
        floatKey(m, "focusOverlayOpacity", Switch::focusOverlayOpacity)

        // ===== TextField（caretColor 已由 ControlValuePropContributor 注册） =====
        colorKey(m, "textColor", TextField::textColor)
        colorKey(m, "placeholderColor", TextField::placeholderColor)
        colorKey(m, "focusBorderColor", TextField::focusBorderColor)
        floatKey(m, "textPaddingX", TextField::textPaddingX)

        // ===== Dropdown =====
        colorKey(m, "textColor", Dropdown::textColor)
        colorKey(m, "arrowColor", Dropdown::arrowColor)
        colorKey(m, "panelColor", Dropdown::panelColor)
        colorKey(m, "optionHoverColor", Dropdown::optionHoverColor)
        colorKey(m, "selectedOptionColor", Dropdown::selectedOptionColor)
        colorKey(m, "selectedOptionTextColor", Dropdown::selectedOptionTextColor)
        colorKey(m, "focusBorderColor", Dropdown::focusBorderColor)
        floatKey(m, "optionHeight", Dropdown::optionHeight)
        colorKey(m, "headerColor", Dropdown::headerColor)
        floatKey(m, "panelCornerRadius", Dropdown::panelCornerRadius)
        floatKey(m, "optionPaddingX", Dropdown::optionPaddingX)
        floatKey(m, "optionPaddingY", Dropdown::optionPaddingY)

        // ===== Image =====
        colorKey(m, "tintColor", Image::tintColor)

        // ===== ItemStack =====
        colorKey(m, "backgroundColor", ItemStack::backgroundColor)
        colorKey(m, "backgroundInnerColor", ItemStack::backgroundInnerColor)
        colorKey(m, "hoverOverlayColor", ItemStack::hoverOverlayColor)
        floatKey(m, "backgroundBorderWidth", ItemStack::backgroundBorderWidth)

        // ===== EntityPreview =====
        colorKey(m, "backgroundColor", EntityPreview::backgroundColor)
        colorKey(m, "hoverOverlayColor", EntityPreview::hoverOverlayColor)

        // ===== TabView（头部标签视觉覆盖：激活/未激活背景与文字、指示线、头部高度、标签内边距）=====
        colorKey(m, "tabActiveBackgroundColor", TabView::tabActiveBackgroundColor)
        colorKey(m, "tabInactiveBackgroundColor", TabView::tabInactiveBackgroundColor)
        colorKey(m, "tabActiveTextColor", TabView::tabActiveTextColor)
        colorKey(m, "tabInactiveTextColor", TabView::tabInactiveTextColor)
        colorKey(m, "tabIndicatorColor", TabView::tabIndicatorColor)
        floatKey(m, "headerHeight", TabView::headerHeight)
        floatKey(m, "tabPadding", TabView::tabPadding)
    }

    /**
     * 注册一个颜色覆盖键：仅当元素是 [E] 时把值转为可空 ARGB 写入 setter；
     * 其它元素类型 no-op（不 warn，避免与同名键在别的控件上互相干扰）。
     *
     * 多个控件复用同名键（如 [focusBorderColor] / [hoverOverlayColor]）：
     * 每次调用以"命中本类型则短路、否则委托上一个 binding"的方式链接，保证按元素类型分发到正确 setter。
     */
    private inline fun <reified E : UIElement<*>> colorKey(
        m: MutableMap<String, PropBinding>,
        key: String,
        crossinline setter: (E, Int?) -> Unit,
    ) {
        register(m, key, E::class) { el, v -> setter(el as E, intColorOrNull(v)) }
    }

    /**
     * 注册一个浮点覆盖键；语义与 [colorKey] 一致，值转为可空 Float。
     */
    private inline fun <reified E : UIElement<*>> floatKey(
        m: MutableMap<String, PropBinding>,
        key: String,
        crossinline setter: (E, Float?) -> Unit,
    ) {
        register(m, key, E::class) { el, v -> setter(el as E, floatOrNull(v)) }
    }

    /**
     * 把单条 type 限定 applier 合并进注册表，遵循"命中本类型则处理并 STOP，否则委托上一个 binding"语义。
     *
     * 这样同名键（如 [hoverOverlayColor]）可在多控件间分发：命中本控件类型即写入并短路，
     * 不会再触发 ButtonState / Slot 等已注册 binding 在不支持类型上打 WARN（它们仅在所有新控件
     * 类型都不命中时作为最终 fallback 执行，保持原有 Button / Slot / Box 行为不变）。read 通道沿用上一个 binding。
     */
    private fun register(m: MutableMap<String, PropBinding>, key: String, type: KClass<*>, mine: PropApplier) {
        val existing = m[key]
        if (existing == null) {
            m[key] = PropBinding.writeOnly { el, v ->
                if (type.isInstance(el)) mine.apply(el, v)
            }
            return
        }
        val prev = existing.applier
        val prevReader = existing.reader
        val combined: PropApplier = { el, v ->
            if (type.isInstance(el)) {
                mine.apply(el, v)
            } else {
                prev.apply(el, v)
            }
        }
        m[key] = PropBinding.of(combined, prevReader)
    }

    /** null 透传：null → null（清除覆盖回退主题）；否则解析为 ARGB int 装箱。 */
    private fun intColorOrNull(v: Any?): Int? = if (v == null) null else ValueCoercion.toIntColor(v)

    /** null 透传：null → null（清除覆盖回退主题）；否则解析为 float 装箱。 */
    private fun floatOrNull(v: Any?): Float? = if (v == null) null else ValueCoercion.toFloat(v)
}
